import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTransition, animated } from '@react-spring/web';
import { useDrag } from 'react-use-gesture';
import { isEditorActive, Placeholder, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import HomepageHeroCarouselComponent from './HomepageHeroCarousel.styles';
import { PlaceholderWrapper, CarouselContainer } from './HomepageHeroCarousel.styles';

import HomepageHeroCarouselSlide from '../HomepageHeroCarouselSlide/HomepageHeroCarouselSlide.jsx';


const HomepageHeroCarousel = ({ ...props }) => {
  const {
    fields = {},
    params = {},
    sitecoreContext,
    rendering = {},
  } = props;

  const { autoplay = '0' } = params;
  const { site } = sitecoreContext;
  const experienceEditor = isEditorActive();
  const carouselItems = rendering?.placeholders?.['homepage-hero-carousel-content'];

  const slides = useMemo(
    () =>
      Array.isArray(carouselItems)
        ? carouselItems.map((item, index) => ({ ...item, slideIndex: index, key: index }))
        : [],
    [carouselItems]
  );
  const [currentSlide, setCurrentSlide] = useState(0); // use 0-based index
  const [direction, setDirection] = useState('next');
  const [pause, setPause] = useState(false);
  const [dynamicMinHeight, setDynamicMinHeight] = useState(null);

  const totalSlides = slides.length;

  const handleSlideResize = useCallback((height) => {
    setDynamicMinHeight(prev => (height && height !== prev ? height : prev));
  }, []);

  // Navigation handlers
  const goToNextSlide = useCallback(() => {
    setDirection('next');
    setCurrentSlide(current => (current === totalSlides - 1 ? 0 : current + 1));
  }, [totalSlides]);

  const goToPrevSlide = useCallback(() => {
    setDirection('prev');
    setCurrentSlide(current => (current > 0 ? current - 1 : totalSlides - 1));
  }, [totalSlides]);

  const transitionValue = useMemo(
    () => ({ slide: slides[currentSlide], direction }),
    [slides, currentSlide, direction]
  );

  // Directional transitions
  const transitions = useTransition(
    transitionValue,
    {
      from: item => ({
        transform: item.direction === 'next' ? 'translateX(100%)' : 'translateX(-100%)',
        opacity: 0,
      }),
      enter: { transform: 'translateX(0%)', opacity: 1 },
      leave: item => ({
        transform: item.direction === 'next' ? 'translateX(-100%)' : 'translateX(100%)',
        opacity: 0,
      }),
      keys: item => `${item.slide.key}-${item.direction}`,
      config: { tension: 300, friction: 30 },
    }
  );

  // Drag handler
  const bind = useDrag(({ down, movement: [mx], direction: [xDir], cancel }) => {
    if (typeof window !== 'undefined') {
      const swipeThreshold = 100;
      if (down && Math.abs(mx) > swipeThreshold) {
        if (xDir > 0) {
          goToPrevSlide();
        } else {
          goToNextSlide();
        }
        cancel();
        setPause(true);
      }
    }
  });

  useEffect(() => {
    if (autoplay === '1' && !experienceEditor) {
      const timer = !pause
        ? setTimeout(() => {
          goToNextSlide();
        }, 5000)
        : null;
      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [currentSlide, pause, autoplay, totalSlides, experienceEditor, goToNextSlide]);

  if (experienceEditor || props?.sitecoreContext?.pageEditing) {
    return (
      <div style={{ width: '100%' }}>
        <div className="home-hero-carousel">
          <div>
            <h1>Start Hero Home Carousel</h1>
          </div>
          <PlaceholderWrapper>
            <Placeholder name="homepage-hero-carousel-content" rendering={rendering} />
          </PlaceholderWrapper>
          <div>
            <h1>End Hero Home Carousel</h1>
          </div>
        </div>
      </div>
    );
  }
  return (
    <CarouselContainer siteName={site?.name} role="region" aria-label="Hero Carousel" aria-roledescription="carousel">
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}
      >
        {totalSlides > 1 && `Slide ${currentSlide + 1} of ${totalSlides}`}
      </div>
      <HomepageHeroCarouselComponent
        site={site?.name}
        className={site?.name}
        style={{ minHeight: dynamicMinHeight ? `${dynamicMinHeight}px` : undefined }}
      >
        {transitions((style, item) => (
          <animated.div style={{ ...style, position: 'absolute', width: '100%', height: '100%' }} key={`${item.slide.key}-${item.direction}`}>
            <HomepageHeroCarouselSlide
              rendering={item.slide}
              setPause={setPause}
              key={item.slide.key}
              bind={bind}
              setCurrentSlide={setCurrentSlide}
              currentSlide={currentSlide}
              totalSlides={totalSlides}
              goToNextSlide={goToNextSlide}
              goToPrevSlide={goToPrevSlide}
              direction={item.direction}
              props={{ ...style, direction: item.direction }}
              isActive={item.slide.slideIndex === currentSlide}
              onResize={handleSlideResize}
              {...fields}
            />
          </animated.div>
        ))}
      </HomepageHeroCarouselComponent>
    </CarouselContainer>
  );
};

HomepageHeroCarousel.propTypes = {
  fields: PropTypes.object,
  params: PropTypes.object,
  sitecoreContext: PropTypes.object,
  rendering: PropTypes.object,
};

export default withSitecoreContext()(HomepageHeroCarousel);